import { useEffect, useRef } from "react";

/**
 * TrustindexWidget
 * ----------------
 * Renders a Trustindex.io reviews slider or certificate.
 *
 * Trustindex's loader script uses `document.currentScript` to know where to
 * render the widget — meaning the <script> MUST be a live, freshly-parsed
 * DOM node with a live parent, at the exact location where the widget
 * should appear. There is no callable render() API.
 *
 * StrictMode-safe strategy:
 *   - Inject a fresh <script> tag into the host container on every mount
 *     (works for initial render AND for route re-entries).
 *   - GUARD against re-injection in the same host: if the host already has
 *     children (from a previous StrictMode mount tick that already loaded
 *     the widget) OR if it already has a loader script pending, skip the
 *     inject. This eliminates duplicates while remaining idempotent.
 *   - NO cleanup on unmount. Trustindex tears down its own DOM when React
 *     removes the host from the tree. Leaving it in place also means
 *     StrictMode's synthetic unmount→remount doesn't lose the widget.
 */

const isReviewsWidgetRendered = (host) => {
  // Trustindex's rendered widget contains many child elements. An unrendered
  // host has either 0 children or just the pending <script> tag.
  const children = host.children;
  if (children.length === 0) return false;
  if (children.length === 1 && children[0].tagName === "SCRIPT") return false;
  return true;
};

export const TrustindexWidget = ({
  widgetId,
  variant = "reviews",
  className = "",
  testId,
}) => {
  const hostRef = useRef(null);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return undefined;

    // Skip injection if the widget is already rendered in this host, or a
    // pending script is present. This makes StrictMode double-invocation
    // idempotent.
    if (isReviewsWidgetRendered(host)) return undefined;
    if (host.querySelector('script[src*="cdn.trustindex.io"]')) return undefined;

    const src =
      variant === "cert"
        ? `https://cdn.trustindex.io/loader-cert.js?${widgetId}`
        : `https://cdn.trustindex.io/loader.js?${widgetId}`;

    const script = document.createElement("script");
    script.src = src;
    script.defer = true;
    script.async = true;
    host.appendChild(script);

    // No cleanup — leaving the widget DOM in place is the whole point.
    return undefined;
  }, [variant, widgetId]);

  return (
    <div
      ref={hostRef}
      data-testid={testId || `trustindex-${variant}`}
      className={className}
    />
  );
};

// Convenience wrappers with the two IDs the client supplied.
export const TrustindexReviews = ({ className, testId }) => (
  <TrustindexWidget
    variant="reviews"
    widgetId="138768580a3f550b2b9666f0792"
    className={className}
    testId={testId || "trustindex-reviews"}
  />
);

export const TrustindexCertificate = ({ className, testId }) => (
  <TrustindexWidget
    variant="cert"
    widgetId="f3ceda08099e55194106274473d"
    className={className}
    testId={testId || "trustindex-cert"}
  />
);

export default TrustindexReviews;
